import { z } from 'zod';
import { deliveryChannelSchema, questionOptionSchema } from '@logistics/contracts';
import type {
  QuestionOption,
  WireScheduleDraft,
  WireScheduleRule,
  WireScheduledPrompt,
} from '@logistics/contracts';

/**
 * The schedule editor as a zod schema. Hour, minute and day of month are the
 * strings a <select> holds; `toDraft` turns them into the API's numbers. The
 * API checks everything again, the timezone included.
 */

export const FREQUENCIES = ['daily', 'weekdays', 'weekly', 'monthly'] as const;
export type Frequency = (typeof FREQUENCIES)[number];

export const FREQUENCY_LABELS: Readonly<Record<Frequency, string>> = {
  daily: 'Every day',
  weekdays: 'Weekdays',
  weekly: 'Weekly',
  monthly: 'Monthly',
};

/** Monday first, as a week is read; the values are the API's (0 is Sunday). */
export const WEEKDAY_CHIPS = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' },
] as const;

const pad = (n: number): string => String(n).padStart(2, '0');
export const HOURS = Array.from({ length: 24 }, (_, h) => pad(h));
export const MINUTES = Array.from({ length: 12 }, (_, i) => pad(i * 5));
export const MONTH_DAYS = Array.from({ length: 31 }, (_, i) => String(i + 1));

export const scheduleFormSchema = z
  .object({
    name: z.string().trim().min(1, 'Give the schedule a name.').max(80, 'At most 80 characters.'),
    prompt: z
      .string()
      .trim()
      .min(1, 'Write the prompt to run.')
      .max(2000, 'At most 2000 characters.'),
    scope: z.array(questionOptionSchema).min(1, 'Pick at least one source to ask.'),
    frequency: z.enum(FREQUENCIES),
    days: z.array(z.number().int().min(0).max(6)),
    dayOfMonth: z.string(),
    hour: z.string(),
    minute: z.string(),
    timezone: z.string().min(1, 'Pick a timezone.'),
    audience: z.enum(['all', 'users']),
    userIds: z.array(z.string()),
    channels: z.array(deliveryChannelSchema).min(1, 'Pick at least one way to deliver it.'),
  })
  .superRefine((form, ctx) => {
    if (form.frequency === 'weekly' && form.days.length === 0) {
      ctx.addIssue({ code: 'custom', path: ['days'], message: 'Pick at least one day.' });
    }
    if (form.audience === 'users' && form.userIds.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['userIds'],
        message: 'Pick at least one recipient, or send to all users.',
      });
    }
  });

export type ScheduleForm = z.input<typeof scheduleFormSchema>;
export type ScheduleValues = z.output<typeof scheduleFormSchema>;

export function emptySchedule(
  timezone: string,
  prefill: { prompt?: string; scope?: QuestionOption[] } = {},
): ScheduleForm {
  return {
    name: '',
    prompt: prefill.prompt ?? '',
    scope: prefill.scope ?? [],
    frequency: 'daily',
    days: [1],
    dayOfMonth: '1',
    hour: '05',
    minute: '00',
    timezone,
    audience: 'all',
    userIds: [],
    channels: ['telegram', 'web'],
  };
}

/** An existing schedule, back into the form's shape. */
export function formOf(schedule: WireScheduledPrompt): ScheduleForm {
  const { rule, recipients } = schedule;
  return {
    name: schedule.name,
    prompt: schedule.prompt,
    scope: [...schedule.scope],
    frequency: rule.kind,
    days: rule.kind === 'weekly' ? [...rule.days] : [1],
    dayOfMonth: rule.kind === 'monthly' ? String(rule.dayOfMonth) : '1',
    hour: pad(rule.hour),
    minute: pad(rule.minute),
    timezone: schedule.timezone,
    audience: recipients.kind,
    userIds: recipients.kind === 'users' ? [...recipients.userIds] : [],
    channels: [...schedule.channels],
  };
}

function ruleOf(values: ScheduleValues): WireScheduleRule {
  const at = { hour: Number(values.hour), minute: Number(values.minute) };
  switch (values.frequency) {
    case 'weekly':
      return { kind: 'weekly', days: values.days as (0 | 1 | 2 | 3 | 4 | 5 | 6)[], ...at };
    case 'monthly':
      return { kind: 'monthly', dayOfMonth: Number(values.dayOfMonth), ...at };
    case 'daily':
    case 'weekdays':
      return { kind: values.frequency, ...at };
  }
}

/** What `POST /schedules` and `PUT /schedules/:id` are sent. */
export function toDraft(values: ScheduleValues): WireScheduleDraft {
  return {
    name: values.name,
    prompt: values.prompt,
    scope: values.scope,
    rule: ruleOf(values),
    timezone: values.timezone,
    recipients:
      values.audience === 'all' ? { kind: 'all' } : { kind: 'users', userIds: values.userIds },
    channels: values.channels,
  };
}

/** Adds a value to a list, or takes it out. */
export function toggled<T>(list: readonly T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

const FIELD_ORDER = ['name', 'prompt', 'scope', 'days', 'timezone', 'channels', 'userIds'] as const;

/** The first field error, in the order the fields are laid out. */
export function firstScheduleError(
  errors: Partial<Record<string, { message?: string | undefined } | undefined>>,
): string | undefined {
  for (const field of FIELD_ORDER) {
    const message = errors[field]?.message;
    if (message !== undefined) return message;
  }
  return undefined;
}

/** "Tue, Mar 3, 05:00" in the schedule's own zone — the time the person set. */
export function zonedTime(iso: string, timezone: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

/** A schedule queued by "run now", or due, that the bot has not run yet. */
export function hasQueued(schedules: readonly WireScheduledPrompt[], at: number): boolean {
  return schedules.some(
    (s) =>
      s.enabled &&
      Date.parse(s.nextRunAt) <= at &&
      (s.lastRunAt === undefined || Date.parse(s.lastRunAt) < Date.parse(s.nextRunAt)),
  );
}
